import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Store,
  Layers,
  Star,
  Zap,
  Phone,
  MapPin,
  BadgeCheck,
  Image,
  ChevronRight,
  Lightbulb,
  Palette,
  Trophy,
  Leaf,
  Heart,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";

/* ─── ANIMATIONS ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const OFFLINE_IMAGE_POOL = [
  "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1559136555-9303baea8ebd?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1535378917042-10a22c95931a?auto=format&fit=crop&w=1200&q=80",
  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80",
];

const getOfflineImage = (index) =>
  OFFLINE_IMAGE_POOL[index % OFFLINE_IMAGE_POOL.length];

const signageServices = [
  {
    name: "3D Letter Signage",
    desc: "Premium dimensional letters for storefronts and offices. Stainless steel, acrylic, brass finishes.",
    placeholder: getOfflineImage(0),
  },
  {
    name: "LED Signage",
    desc: "Energy-efficient illuminated signs. Custom colors, animations, brightness control.",
    placeholder: getOfflineImage(1),
  },
  {
    name: "ACP Sign Boards",
    desc: "Durable weather-resistant boards. Ideal for outdoor branding and direction signs.",
    placeholder: getOfflineImage(2),
  },
  {
    name: "Glow Sign Boards",
    desc: "Backlit flex and acrylic boards. Perfect for shopfronts and restaurants.",
    placeholder: getOfflineImage(3),
  },
  {
    name: "Wayfinding Signage",
    desc: "Intuitive directional signs for malls, hospitals, campuses. ADA-compliant.",
    placeholder: getOfflineImage(4),
  },
  {
    name: "Reception Signage",
    desc: "Custom lobby signs. Backlit, raised, or flush-mounted designs.",
    placeholder: getOfflineImage(5),
  },
  {
    name: "Building Facade Signage",
    desc: "Large-scale exterior signage. Engineered for durability and visibility.",
    placeholder: getOfflineImage(6),
  },
];

const decorServices = [
  {
    name: "Wall Graphics",
    desc: "Vinyl, fabric, or direct-to-wall murals. High-resolution branded canvases.",
    placeholder: getOfflineImage(7),
  },
  {
    name: "Retail Branding",
    desc: "End-to-end in-store implementation. Shelf talkers to checkout displays.",
    placeholder: getOfflineImage(8),
  },
  {
    name: "Fabric Smart Frames",
    desc: "Sleek aluminum frames with dye-sublimated fabric. Easy seasonal updates.",
    placeholder: getOfflineImage(9),
  },
  {
    name: "Window Graphics",
    desc: "One-way vision films, perforated vinyl. Prime advertising real estate.",
    placeholder: getOfflineImage(10),
  },
  {
    name: "Frosted Glass Films",
    desc: "Privacy films with custom branding. Perfect for offices and storefronts.",
    placeholder: getOfflineImage(11),
  },
  {
    name: "Custom Display Graphics",
    desc: "POS displays, standees, modular exhibition graphics.",
    placeholder: getOfflineImage(2),
  },
];

/* ─── HERO SECTION ─── */
const HeroSection = () => (
  <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-amber-50/80 via-orange-50/40 to-transparent pointer-events-none" />
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-amber-300/20 rounded-full blur-[120px] animate-float-orb" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-300/15 rounded-full blur-[120px] animate-float-orb-reverse" />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={stagger}
        className="max-w-2xl"
      >
        <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
          <Link
            to="/services"
            className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
          >
            Services
          </Link>
          <span className="text-slate-300">/</span>
          <span className="text-sm text-amber-600 font-semibold">
            Offline Marketing
          </span>
        </motion.div>

        <motion.h1
          variants={fadeUp}
          custom={1}
          className="text-5xl sm:text-6xl md:text-7xl font-black leading-tight text-slate-900 mb-6"
        >
          Make your brand{" "}
          <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
            impossible to miss
          </span>
        </motion.h1>

        <motion.p
          variants={fadeUp}
          custom={2}
          className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8"
        >
          Professional signage, premium materials, and expert installation.
          Trusted by 200+ businesses to elevate their brand presence in the real
          world.
        </motion.p>

        <motion.div
          variants={fadeUp}
          custom={3}
          className="flex flex-wrap gap-4"
        >
          <Link
            to="/contact"
            className="btn-shine inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-apple-lg hover:scale-[1.02] transition-all"
          >
            Schedule Consultation <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#services"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-amber-200 text-amber-700 font-semibold hover:bg-amber-50 transition-all"
          >
            View Our Work <ChevronRight className="w-4 h-4" />
          </a>
        </motion.div>

        {/* Trust stats inline */}
        <motion.div
          variants={fadeUp}
          custom={4}
          className="mt-12 pt-8 border-t border-slate-200 flex flex-wrap gap-8"
        >
          <div>
            <div className="text-2xl font-black text-slate-900">48+</div>
            <div className="text-sm text-slate-500">Years Excellence</div>
          </div>
          <div>
            <div className="text-2xl font-black text-slate-900">200+</div>
            <div className="text-sm text-slate-500">Global Clients</div>
          </div>
          <div>
            <div className="text-2xl font-black text-slate-900">Pan-India</div>
            <div className="text-sm text-slate-500">Coverage</div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

/* ─── WHY OFFLINE ─── */
const WhyOfflineSection = () => {
  const reasons = [
    {
      icon: MapPin,
      title: "Local Dominance",
      desc: "Be the most visible business in your area with signage that commands attention 24/7.",
    },
    {
      icon: BadgeCheck,
      title: "Trust & Credibility",
      desc: "Professional signage signals permanence and reliability — customers trust invested brands.",
    },
    {
      icon: Zap,
      title: "Instant Impact",
      desc: "Unlike digital ads, physical branding works immediately. No algorithms, no ad fatigue.",
    },
    {
      icon: Star,
      title: "Premium Quality",
      desc: "UV-resistant prints, marine-grade stainless steel, commercial-grade LEDs.",
    },
  ];

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-transparent via-amber-50/30 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            Why physical branding{" "}
            <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              still wins
            </span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500 leading-relaxed"
          >
            In a world of digital noise, physical branding creates tangible
            touchpoints that no screen can replicate.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((r, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className="glass-strong rounded-2xl p-6 shadow-apple hover:shadow-apple-lg transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-4">
                <r.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                {r.title}
              </h3>
              <p className="text-sm text-slate-500 leading-relaxed">{r.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── OUR CAPABILITY ─── */
const OurCapabilitySection = () => {
  const metrics = [
    {
      icon: Store,
      stat: "150,000+",
      label: "Sq Ft Manufacturing",
      detail: "State-of-the-art facility",
    },
    {
      icon: BadgeCheck,
      stat: "450+",
      label: "Skilled Team Members",
      detail: "Experts in every role",
    },
    {
      icon: Zap,
      stat: "25+",
      label: "Machines & Equipment",
      detail: "Latest technology",
    },
    {
      icon: Trophy,
      stat: "48+",
      label: "Years Excellence",
      detail: "Industry leader",
    },
  ];

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-amber-50/30 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            Built to Deliver
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500"
          >
            State-of-the-art infrastructure, decades of expertise
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((m, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className="glass-strong rounded-2xl p-6 shadow-apple"
            >
              <m.icon className="w-10 h-10 text-amber-600 mb-4" />
              <div className="text-3xl font-bold text-slate-900">{m.stat}</div>
              <div className="text-sm font-semibold text-slate-900 mt-2">
                {m.label}
              </div>
              <p className="text-xs text-slate-500 mt-2">{m.detail}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── WHY CHOOSE US ─── */
const WhyChooseUsSection = () => {
  const pillars = [
    {
      icon: Lightbulb,
      title: "Deep Expertise",
      desc: "Decades of experience in signage and branding.",
    },
    {
      icon: Palette,
      title: "Full Customization",
      desc: "Every project custom-designed for your brand.",
    },
    {
      icon: Trophy,
      title: "Premium Quality",
      desc: "Industry-grade materials and professional installation.",
    },
    {
      icon: Leaf,
      title: "Sustainable",
      desc: "Eco-friendly materials and ethical practices.",
    },
    {
      icon: Heart,
      title: "Your Success",
      desc: "Dedicated to exceeding expectations.",
    },
  ];

  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            Why Choose Us
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500 max-w-2xl mx-auto"
          >
            Decades of expertise, commitment to excellence
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {pillars.map((p, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className="glass-strong rounded-2xl p-6 shadow-apple hover:shadow-apple-lg transition-all"
            >
              <p.icon className="w-10 h-10 text-amber-600 mb-3" />
              <h3 className="font-bold text-slate-900 mb-2">{p.title}</h3>
              <p className="text-sm text-slate-500">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── SERVICES TABS ─── */
const ServicesSection = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [activeService, setActiveService] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  const tabs = [
    { label: "Signage Solutions", services: signageServices },
    { label: "In-Store Decor", services: decorServices },
  ];

  const currentServices = tabs[activeTab].services;
  const currentService = currentServices[activeService];
  const imageKey = `${activeTab}-${activeService}`;
  const hasImageError = imageErrors[imageKey];

  return (
    <section
      id="services"
      className="py-20 sm:py-28 bg-gradient-to-b from-amber-50/40 to-transparent"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            Our Services
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500"
          >
            Comprehensive signage and branding solutions
          </motion.p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={2}
          className="flex gap-2 mb-12 border-b border-slate-200"
        >
          {tabs.map((tab, i) => (
            <button
              key={i}
              onClick={() => {
                setActiveTab(i);
                setActiveService(0);
              }}
              className={`px-6 py-3 font-semibold transition-all border-b-2 ${
                activeTab === i
                  ? "border-amber-600 text-amber-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Service List */}
          <div className="lg:col-span-1 space-y-2">
            {currentServices.map((svc, i) => (
              <button
                key={i}
                onClick={() => setActiveService(i)}
                className={`w-full text-left p-4 rounded-lg transition-all ${
                  activeService === i
                    ? "glass-strong border border-amber-200/60 bg-white"
                    : "hover:bg-amber-50"
                }`}
              >
                <span
                  className={`font-semibold ${
                    activeService === i ? "text-slate-900" : "text-slate-600"
                  }`}
                >
                  {svc.name}
                </span>
              </button>
            ))}
          </div>

          {/* Service Detail */}
          <motion.div
            key={activeService}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="lg:col-span-3"
          >
            <div className="glass-strong rounded-2xl overflow-hidden shadow-apple p-6">
              {/* Image */}
              <div className="relative rounded-xl overflow-hidden mb-6 aspect-video bg-gradient-to-br from-amber-100 to-orange-50">
                {!hasImageError ? (
                  <img
                    src={currentService.placeholder}
                    alt={currentService.name}
                    className="w-full h-full object-cover"
                    onError={() =>
                      setImageErrors((prev) => ({
                        ...prev,
                        [imageKey]: true,
                      }))
                    }
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <Image className="w-12 h-12 text-amber-400 mb-3" />
                    <p className="font-semibold text-amber-800">
                      {currentService.name}
                    </p>
                  </div>
                )}
              </div>

              {/* Details */}
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {currentService.name}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {currentService.desc}
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:text-amber-700 transition-colors"
              >
                Get Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─── CLIENT SHOWCASE ─── */
const ClientShowcaseSection = () => {
  const clients = [
    "Tesla",
    "Reliance",
    "Indian Oil",
    "Jio",
    "HDFC",
    "ICICI",
    "McDonald's",
    "Starbucks",
    "Microsoft",
    "Google",
    "Amazon",
    "Apple",
  ];

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-amber-50/40 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            Trusted by Leading Brands
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500"
          >
            200+ businesses across 15+ industries
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4"
        >
          {clients.map((client, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              custom={i}
              className="glass-strong rounded-xl p-4 shadow-apple flex items-center justify-center h-20"
            >
              <p className="text-sm font-bold text-slate-900 text-center">
                {client}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ─── PORTFOLIO ─── */
const PortfolioSection = () => {
  const categories = [
    { label: "3D Letters", img: 4 },
    { label: "LED Signs", img: 5 },
    { label: "Wall Graphics", img: 6 },
    { label: "Retail Branding", img: 7 },
    { label: "Reception", img: 8 },
    { label: "Facade", img: 9 },
  ];

  const [imageErrors, setImageErrors] = useState({});

  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            Our Work
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500"
          >
            Real projects for real businesses
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map((cat, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className={`relative rounded-2xl overflow-hidden shadow-apple group cursor-pointer bg-gradient-to-br from-amber-100 to-orange-50 ${
                i === 0 || i === 5
                  ? "md:row-span-2 aspect-[3/4]"
                  : "aspect-square"
              }`}
            >
              {!imageErrors[i] ? (
                <img
                  src={getOfflineImage(cat.img)}
                  alt={cat.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={() =>
                    setImageErrors((prev) => ({ ...prev, [i]: true }))
                  }
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <Image className="w-8 h-8 text-amber-400 mb-2" />
                  <p className="text-sm font-semibold text-amber-800">
                    {cat.label}
                  </p>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-sm font-semibold text-white">{cat.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── SERVICE PACKAGES ─── */
const ServicePackagesSection = () => {
  const packages = [
    {
      icon: Store,
      title: "Retail Transformation",
      desc: "Complete in-store branding overhaul",
      features: [
        "Facade signage",
        "In-store graphics",
        "Display solutions",
        "Installation",
      ],
      timeline: "30-45 days",
    },
    {
      icon: Trophy,
      title: "Corporate Identity",
      desc: "Premium office branding",
      features: [
        "Reception signage",
        "Office branding",
        "Wayfinding system",
        "Installation",
      ],
      timeline: "21-35 days",
    },
    {
      icon: Zap,
      title: "Hospitality Enhancement",
      desc: "Premium branding for F&B",
      features: [
        "Facade signage",
        "Interior graphics",
        "Wayfinding signs",
        "Lighting",
      ],
      timeline: "25-40 days",
    },
    {
      icon: Store,
      title: "Franchise Rollout",
      desc: "Multi-location standardized branding",
      features: [
        "Design templates",
        "Multi-location coordination",
        "QA across sites",
        "Maintenance",
      ],
      timeline: "Custom timeline",
    },
  ];

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-amber-50/40 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            Service Packages
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500"
          >
            Comprehensive solutions for your needs
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {packages.map((pkg, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className="glass-strong rounded-2xl p-8 shadow-apple hover:shadow-apple-lg transition-all"
            >
              <pkg.icon className="w-10 h-10 text-amber-600 mb-4" />
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                {pkg.title}
              </h3>
              <p className="text-slate-600 text-sm mb-4">{pkg.desc}</p>

              <ul className="space-y-2 mb-6">
                {pkg.features.map((f, j) => (
                  <li key={j} className="flex gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="text-xs text-amber-600 font-semibold">
                {pkg.timeline}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── CUSTOM SOLUTIONS ─── */
const CustomSolutionsSection = () => {
  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            Custom Solutions
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500"
          >
            Transparent process, tailored pricing
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={2}
          className="glass-strong rounded-2xl p-10 shadow-apple mb-10"
        >
          <p className="text-slate-600 leading-relaxed mb-8">
            Every project is unique. We offer custom proposals based on your
            requirements, scope, and timeline.
          </p>

          <h3 className="font-bold text-slate-900 mb-4">What's included:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {[
              "Project assessment",
              "Design mockups",
              "Material & labor breakdown",
              "Installation timeline",
              "Warranty options",
              "Flexible payment terms",
            ].map((item, i) => (
              <div key={i} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <span className="text-slate-600">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          custom={3}
          className="text-center"
        >
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:shadow-apple-lg transition-all"
          >
            Request Proposal
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

/* ─── PROCESS ─── */
const ProcessSection = () => {
  const steps = [
    {
      num: "01",
      title: "Consultation",
      desc: "Site visit, understand goals",
      time: "1-2 hours",
    },
    {
      num: "02",
      title: "Design & Quote",
      desc: "Custom mockups, detailed proposal",
      time: "5-7 days",
    },
    {
      num: "03",
      title: "Fabrication",
      desc: "Premium materials, precision work",
      time: "14-21 days",
    },
    {
      num: "04",
      title: "Installation",
      desc: "Professional installation",
      time: "3-7 days",
    },
  ];

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-amber-50/40 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900"
          >
            How It Works
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={1}
            className="mt-4 text-slate-500"
          >
            A transparent, professional process
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              className="glass-strong rounded-2xl p-6 shadow-apple relative"
            >
              <div className="text-4xl font-black bg-gradient-to-br from-amber-500 to-orange-500 bg-clip-text text-transparent mb-4">
                {step.num}
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
              <p className="text-sm text-slate-500 mb-4">{step.desc}</p>
              <div className="text-xs text-amber-600 font-semibold">
                {step.time}
              </div>
              {i < 3 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-amber-300" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── FINAL CTA ─── */
const FinalCTASection = () => (
  <section className="py-20 sm:py-28">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 p-12 sm:p-20 text-center shadow-apple-xl"
      >
        <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />

        <div className="relative z-10">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Ready to elevate your brand?
          </h2>
          <p className="text-lg text-white/70 max-w-xl mx-auto mb-10">
            Book a free consultation with our experts. We'll assess your space
            and present tailored solutions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact"
              className="btn-shine inline-flex items-center gap-2 px-8 py-4 bg-white text-amber-700 font-semibold rounded-xl hover:shadow-apple-lg transition-all"
            >
              Schedule Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#services"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-all"
            >
              Explore Services
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ─── PAGE ─── */
const OfflineMarketingPage = () => (
  <div className="min-h-screen bg-[#FAFAFE]">
    <Header />
    <HeroSection />
    <WhyOfflineSection />
    <OurCapabilitySection />
    <WhyChooseUsSection />
    <ServicesSection />
    <ClientShowcaseSection />
    <PortfolioSection />
    <ServicePackagesSection />
    <CustomSolutionsSection />
    <ProcessSection />
    <FinalCTASection />
    <Footer />
  </div>
);

export default OfflineMarketingPage;
